/**
 * I3M Probability Tables
 * 
 * Tabelle di probabilità per la stima dell'età basata sull'indice I3M (Third Molar Maturity Index).
 * Derivate dallo studio: Angelakopoulos N, De Luca S, Oliveira-Santos I, et al.
 * "Third molar maturity index (I3M) assessment according to different geographical zones"
 * Int J Legal Med. 2023;137(2):403–425
 */

// ============================================================================
// TABELLE DI PROBABILITÀ PER REGIONE
// ============================================================================

/**
 * Tabella Europa - Cut-off: 0.09, Specificità: 93%, Sensibilità: 79%
 */
export const europeTable = [
  { i3m: 0.00, pAdult: 0.950, pMinor: 0.050 },
  { i3m: 0.01, pAdult: 0.948, pMinor: 0.052 },
  { i3m: 0.02, pAdult: 0.944, pMinor: 0.056 },
  { i3m: 0.03, pAdult: 0.937, pMinor: 0.063 },
  { i3m: 0.04, pAdult: 0.927, pMinor: 0.073 },
  { i3m: 0.05, pAdult: 0.913, pMinor: 0.087 },
  { i3m: 0.06, pAdult: 0.893, pMinor: 0.107 },
  { i3m: 0.07, pAdult: 0.865, pMinor: 0.135 },
  { i3m: 0.08, pAdult: 0.825, pMinor: 0.175 },
  { i3m: 0.09, pAdult: 0.771, pMinor: 0.229 }, // CUT-OFF
  { i3m: 0.10, pAdult: 0.698, pMinor: 0.302 },
  { i3m: 0.11, pAdult: 0.606, pMinor: 0.394 },
  { i3m: 0.12, pAdult: 0.496, pMinor: 0.504 }, // SOGLIA 50%
  { i3m: 0.13, pAdult: 0.377, pMinor: 0.623 },
  { i3m: 0.14, pAdult: 0.265, pMinor: 0.735 },
  { i3m: 0.15, pAdult: 0.171, pMinor: 0.829 },
  { i3m: 0.16, pAdult: 0.102, pMinor: 0.898 },
  { i3m: 0.17, pAdult: 0.057, pMinor: 0.943 },
  { i3m: 0.18, pAdult: 0.030, pMinor: 0.970 },
  { i3m: 0.19, pAdult: 0.015, pMinor: 0.985 },
  { i3m: 0.20, pAdult: 0.007, pMinor: 0.993 },
];

/**
 * Tabella Asia - Cut-off: 0.15, Specificità: 89%, Sensibilità: 91%
 * Nota: Curva molto piatta, mai sotto il 50% nel range 0-0.20
 */
export const asiaTable = [
  { i3m: 0.00, pAdult: 0.878, pMinor: 0.122 },
  { i3m: 0.01, pAdult: 0.879, pMinor: 0.121 },
  { i3m: 0.02, pAdult: 0.879, pMinor: 0.121 },
  { i3m: 0.03, pAdult: 0.878, pMinor: 0.122 },
  { i3m: 0.04, pAdult: 0.876, pMinor: 0.124 },
  { i3m: 0.05, pAdult: 0.874, pMinor: 0.126 },
  { i3m: 0.06, pAdult: 0.870, pMinor: 0.130 },
  { i3m: 0.07, pAdult: 0.866, pMinor: 0.134 },
  { i3m: 0.08, pAdult: 0.862, pMinor: 0.138 },
  { i3m: 0.09, pAdult: 0.856, pMinor: 0.144 },
  { i3m: 0.10, pAdult: 0.849, pMinor: 0.151 },
  { i3m: 0.11, pAdult: 0.841, pMinor: 0.159 },
  { i3m: 0.12, pAdult: 0.832, pMinor: 0.168 },
  { i3m: 0.13, pAdult: 0.822, pMinor: 0.178 },
  { i3m: 0.14, pAdult: 0.810, pMinor: 0.190 },
  { i3m: 0.15, pAdult: 0.796, pMinor: 0.204 }, // CUT-OFF
  { i3m: 0.16, pAdult: 0.781, pMinor: 0.219 },
  { i3m: 0.17, pAdult: 0.764, pMinor: 0.236 },
  { i3m: 0.18, pAdult: 0.744, pMinor: 0.256 },
  { i3m: 0.19, pAdult: 0.723, pMinor: 0.277 },
  { i3m: 0.20, pAdult: 0.699, pMinor: 0.301 },
];

/**
 * Tabella Africa - Cut-off: 0.10, Specificità: 87%, Sensibilità: 92%
 */
export const africaTable = [
  { i3m: 0.00, pAdult: 0.872, pMinor: 0.128 },
  { i3m: 0.01, pAdult: 0.872, pMinor: 0.128 },
  { i3m: 0.02, pAdult: 0.867, pMinor: 0.133 },
  { i3m: 0.03, pAdult: 0.858, pMinor: 0.142 },
  { i3m: 0.04, pAdult: 0.845, pMinor: 0.155 },
  { i3m: 0.05, pAdult: 0.825, pMinor: 0.175 },
  { i3m: 0.06, pAdult: 0.797, pMinor: 0.203 },
  { i3m: 0.07, pAdult: 0.761, pMinor: 0.239 },
  { i3m: 0.08, pAdult: 0.713, pMinor: 0.287 },
  { i3m: 0.09, pAdult: 0.651, pMinor: 0.349 },
  { i3m: 0.10, pAdult: 0.576, pMinor: 0.424 }, // CUT-OFF
  { i3m: 0.11, pAdult: 0.489, pMinor: 0.511 }, // SOGLIA 50%
  { i3m: 0.12, pAdult: 0.394, pMinor: 0.606 },
  { i3m: 0.13, pAdult: 0.299, pMinor: 0.701 },
  { i3m: 0.14, pAdult: 0.212, pMinor: 0.788 },
  { i3m: 0.15, pAdult: 0.142, pMinor: 0.858 },
  { i3m: 0.16, pAdult: 0.089, pMinor: 0.911 },
  { i3m: 0.17, pAdult: 0.053, pMinor: 0.947 },
  { i3m: 0.18, pAdult: 0.030, pMinor: 0.970 },
  { i3m: 0.19, pAdult: 0.016, pMinor: 0.984 },
  { i3m: 0.20, pAdult: 0.008, pMinor: 0.992 },
];

/**
 * Tabella America - Cut-off: 0.10, Specificità: 90%, Sensibilità: 86%
 */
export const americaTable = [
  { i3m: 0.00, pAdult: 0.917, pMinor: 0.083 },
  { i3m: 0.01, pAdult: 0.916, pMinor: 0.084 },
  { i3m: 0.02, pAdult: 0.912, pMinor: 0.088 },
  { i3m: 0.03, pAdult: 0.906, pMinor: 0.094 },
  { i3m: 0.04, pAdult: 0.895, pMinor: 0.105 },
  { i3m: 0.05, pAdult: 0.879, pMinor: 0.121 },
  { i3m: 0.06, pAdult: 0.858, pMinor: 0.142 },
  { i3m: 0.07, pAdult: 0.828, pMinor: 0.172 },
  { i3m: 0.08, pAdult: 0.787, pMinor: 0.213 },
  { i3m: 0.09, pAdult: 0.732, pMinor: 0.268 },
  { i3m: 0.10, pAdult: 0.661, pMinor: 0.339 }, // CUT-OFF
  { i3m: 0.11, pAdult: 0.573, pMinor: 0.427 },
  { i3m: 0.12, pAdult: 0.472, pMinor: 0.528 }, // SOGLIA 50%
  { i3m: 0.13, pAdult: 0.364, pMinor: 0.636 },
  { i3m: 0.14, pAdult: 0.261, pMinor: 0.739 },
  { i3m: 0.15, pAdult: 0.174, pMinor: 0.826 },
  { i3m: 0.16, pAdult: 0.108, pMinor: 0.892 },
  { i3m: 0.17, pAdult: 0.063, pMinor: 0.937 },
  { i3m: 0.18, pAdult: 0.035, pMinor: 0.965 },
  { i3m: 0.19, pAdult: 0.018, pMinor: 0.982 },
  { i3m: 0.20, pAdult: 0.009, pMinor: 0.991 },
];

// ============================================================================
// STRUTTURA DATI ETNIE
// ============================================================================

/**
 * Configurazione completa per ogni etnia/regione
 */
export const ethnicityData = {
  Europe: {
    name: 'Europe',
    label: 'Europa',
    flag: '🇪🇺',
    table: europeTable,
    cutoff: 0.09,
    sensitivity: 0.79,
    specificity: 0.93,
    threshold50: 0.12, // I3M dove P(Adult) ≈ 50%
  },
  Asia: {
    name: 'Asia',
    label: 'Asia',
    flag: '🌏',
    table: asiaTable,
    cutoff: 0.15,
    sensitivity: 0.91,
    specificity: 0.89,
    threshold50: null, // Mai sotto 50% nel range 0-0.20
  },
  Africa: {
    name: 'Africa',
    label: 'Africa',
    flag: '🌍',
    table: africaTable,
    cutoff: 0.10,
    sensitivity: 0.92,
    specificity: 0.87,
    threshold50: 0.11,
  },
  America: {
    name: 'America',
    label: 'America',
    flag: '🌎',
    table: americaTable,
    cutoff: 0.10,
    sensitivity: 0.86,
    specificity: 0.90,
    threshold50: 0.12,
  },
  // Golden Standard / Unknown - usa Europa come default conservativo
  Unknown: {
    name: 'Unknown',
    label: 'Unknown / Golden Standard',
    flag: '🗺️',
    table: europeTable,
    cutoff: 0.10,
    sensitivity: 0.79,
    specificity: 0.93,
    threshold50: 0.12,
  },
};

// ============================================================================
// FUNZIONI DI UTILITÀ
// ============================================================================

/**
 * Crea una mappa di lookup per accesso O(1) alle probabilità
 * La chiave è i3m * 100 (es. 0.09 → 9) per evitare problemi floating point
 * 
 * @param {Array} table - Tabella di probabilità
 * @returns {Map} Mappa chiave→pAdult
 */
export function createLookupMap(table) {
  const map = new Map();
  table.forEach(entry => {
    const key = Math.round(entry.i3m * 100);
    map.set(key, entry.pAdult);
  });
  return map;
}

// Pre-calcola le mappe di lookup per ogni regione
export const lookupMaps = {
  Europe: createLookupMap(europeTable),
  Asia: createLookupMap(asiaTable),
  Africa: createLookupMap(africaTable),
  America: createLookupMap(americaTable),
  Unknown: createLookupMap(europeTable),
};

/**
 * Ottiene la tabella e i parametri per una specifica regione
 * 
 * @param {string} region - Nome della regione (Europe, Asia, Africa, America)
 * @returns {Object} Dati dell'etnia o Unknown se non trovata
 */
export function getEthnicityParams(region) {
  return ethnicityData[region] || ethnicityData.Unknown;
}

/**
 * Ottiene la lookup map per una specifica regione
 * 
 * @param {string} region - Nome della regione
 * @returns {Map} Mappa di lookup
 */
export function getLookupMap(region) {
  return lookupMaps[region] || lookupMaps.Unknown;
}

/**
 * Lista delle regioni disponibili per UI
 */
export const availableRegions = [
  { value: 'Europe', label: 'Europa', flag: '🇪🇺' },
  { value: 'Asia', label: 'Asia', flag: '🌏' },
  { value: 'Africa', label: 'Africa', flag: '🌍' },
  { value: 'America', label: 'America', flag: '🌎' },
];

export default {
  europeTable,
  asiaTable,
  africaTable,
  americaTable,
  ethnicityData,
  lookupMaps,
  createLookupMap,
  getEthnicityParams,
  getLookupMap,
  availableRegions,
};
